import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Paper from './layout/Paper';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

export function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`vertical-tabpanel-${index}`}
			aria-labelledby={`vertical-tab-${index}`}
			style={{ flex: 1 }}
			{...other}
		>
			{value === index && (
				<Box>
					<Typography>{children}</Typography>
				</Box>
			)}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `vertical-tab-${index}`,
		'aria-controls': `vertical-tabpanel-${index}`,
	};
}

interface VerticalTabsProps {
	tabs: { label: string; content: React.ReactNode }[];
	value: number;
	setValue: (value: number) => void;
	showTabs?: boolean;
	initialTab?: number;
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
	'& .MuiTabs-indicator': {
		display: 'none', // Hide the default indicator since we'll use a custom border
	},
	'& .MuiTab-root': {
		color: theme.palette.primary.main, // Blue text color
		fontWeight: 500,
		textTransform: 'none', // Keep original case
		alignItems: 'flex-start',
		textAlign: 'left',
		justifyContent: 'center',
		minHeight: '48px',
		maxWidth: '280px',
		margin: '4px 24px 4px 0',
		borderRadius: '8px',
		border: '1px solid transparent',
		transition: 'all 0.2s ease-in-out',
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
		'&.Mui-selected': {
			color: theme.palette.primary.main,
			backgroundColor: '#212E44', // Light blue background
			border: `1px solid ${theme.palette.primary.main}`, // Blue border
			fontWeight: 600,
		},
	},
}));

const VerticalTabs: React.FC<VerticalTabsProps> = ({
	tabs,
	value,
	setValue,
	showTabs = true,
	initialTab = 0,
}) => {
	// const [value, setValue] = React.useState(initialTab);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	return (
		<Box
			sx={{
				flexGrow: 1,
				bgcolor: 'transparent',
				display: 'flex',
				height: '100%',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					minWidth: 'fit-content',
					maxWidth: '300px',
				}}
			>
				<StyledTabs
					orientation="vertical"
					variant="scrollable"
					scrollButtons="auto"
					value={value}
					onChange={handleChange}
					aria-label="Vertical tabs example"
					sx={{
						borderRight: 1,
						borderWidth: 0,
						marginRight: 1,
						minWidth: 'fit-content',
						height: '100%',
						'& .MuiTabs-scroller': {
							height: '100%',
						},
						'& .MuiTabs-flexContainer': {
							height: '100%',
							alignItems: 'flex-start',
						},
					}}
				>
					{tabs.map((tab, index) => (
						<Tab
							key={index}
							label={tab.label}
							{...a11yProps(index)}
						/>
					))}
				</StyledTabs>
			</Box>
			{showTabs &&
				tabs.map((tab, index) => (
					<TabPanel key={index} value={value} index={index}>
						{tab.content}
					</TabPanel>
				))}
		</Box>
	);
};

export default VerticalTabs;
